import React, { useState } from 'react';
import { BookOpenIcon, FileSpreadsheetIcon, HouseIcon, LayoutDashboardIcon } from 'lucide-react';
import { howSteps } from '../data/howItWorks';
import { TL_UNIT_COUNT_REVIEW_NOTE } from '../data/periodClose';
import { BONUS_CAP_NOTE, BONUS_SANDBOX_NOTE, TIER_LABEL_NOTE, monthlyBonusBands, statementMeta, troyBaseBands } from '../data/statement';
import { RiskNote } from '../components/sf/RiskNote';
import { Button } from '../components/sf/Button';
import { ExampleDataBadge } from '../components/sf/ExampleDataBadge';
import { RecordHeader } from '../components/sf/RecordHeader';
import { AdjustedEquation } from '../components/howItWorks/AdjustedEquation';
import { BonusComparison } from '../components/howItWorks/BonusComparison';
import { PaidTotalCallout } from '../components/howItWorks/PaidTotalCallout';
import { CadenceLegend } from '../components/howItWorks/CadenceLegend';
import { FlowRail } from '../components/howItWorks/FlowRail';
import { LinesVisual } from '../components/howItWorks/LinesVisual';
import { LockVisual } from '../components/howItWorks/LockVisual';
import { PrecedenceChain } from '../components/howItWorks/PrecedenceChain';
import { RollupVisual } from '../components/howItWorks/RollupVisual';
import { StepCard } from '../components/howItWorks/StepCard';
import { BandBar } from '../components/statement/BandBar';
import { ScreenId } from '../types/navigation';
interface HowCommissionsWorkProps {
  onNavigate: (screen: ScreenId) => void;
}
export function HowCommissionsWork({
  onNavigate
}: HowCommissionsWorkProps) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(['funds']));
  const {
    volume,
    units
  } = statementMeta;
  const toggle = (id: string) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);else next.add(id);
    return next;
  });
  const jumpTo = (id: string) => {
    setExpanded(prev => new Set(prev).add(id));
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.getElementById(`step-${id}`)?.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'start'
    });
  };
  const visuals: Record<string, React.ReactNode> = {
    plan: <div className="space-y-2" data-id="element-1153" data-mp="2ozekb3pg8b8t">
        <PrecedenceChain data-id="element-1154" data-mp="2u13s8ahdk8so" />
        <p className="text-xs text-sf-weak" data-id="element-1155" data-mp="2tzamqyihzum9">Each commission line stores a snapshot of the plan version and inputs used, so it can always be explained later.</p>
      </div>,
    base: <div className="space-y-2" data-id="element-1156" data-mp="21cy73mmeshsb">
        <BandBar bands={troyBaseBands} value={volume / 1000000} scaleMax={3} markerLabel="Alex · Troy · $2,485,400.00" ariaLabel="Troy period volume $2,485,400.00 places Alex at 125 bps" data-id="element-1157" data-mp="219two7icopt7" />
        <p className="text-xs text-sf-weak" data-id="element-1158" data-mp="1dmvg79u8dhva">Miami: 100, or 150 bps at ≥ $2,000,000.00 · Arizona: flat 100 bps.</p>
        <RiskNote data-id="element-1159" data-mp="1ikdiwihnn0uu" />
        <p className="text-[11px] text-sf-weak" data-id="element-1160" data-mp="1ijudg196y4xp">{TIER_LABEL_NOTE}</p>
      </div>,
    bonus: <div className="space-y-4" data-id="element-1161" data-mp="1b1e1gidr7je4">
        <BandBar bands={monthlyBonusBands} value={units} scaleMax={9} discrete markerLabel={`Alex · ${units} closed loans`} ariaLabel={`Alex · ${units} closed loans in October gives a 0 bps bonus`} data-id="element-1162" data-mp="162tbrcw4oi3x" />
        <p className="text-xs text-sf-weak" data-id="element-1163" data-mp="x1v50mub1whn">
          7 closed loans → 7+ band value is 0 (main); cap not binding. {BONUS_CAP_NOTE} <span className="text-[11px]" data-id="element-1164" data-mp="s4v0igd3t1sn">{BONUS_SANDBOX_NOTE}</span>
        </p>
        <BonusComparison data-id="element-1165" data-mp="nb0jjkovv0x9" />
        <p className="rounded border border-[#F9C88C] bg-sf-amber-light px-3 py-2 text-xs text-sf-text" data-id="element-1166" data-mp="id8fjsayrocp">
          {TL_UNIT_COUNT_REVIEW_NOTE}
        </p>
      </div>,
    adjusted: <AdjustedEquation data-id="element-1167" data-mp="dc2m1s9hbx24" />,
    lines: <LinesVisual data-id="element-1168" data-mp="9cakhzii6r69" />,
    rollup: <RollupVisual data-id="element-1169" data-mp="9biowoowotfz" />,
    lock: <LockVisual data-id="element-1170" data-mp="9br08c5drqp2" />
  };
  return <div className="space-y-3" data-id="element-1171" data-mp="4dpj3wrim1g8">
      <RecordHeader objectLabel="Guide" title="How Commissions Work" icon={BookOpenIcon} iconBg="#032D60" titleAddon={<ExampleDataBadge data-id="element-1173" data-mp="24i2xs4k6b70i" />} actions={<>
            <Button icon={<FileSpreadsheetIcon data-id="element-1175" data-mp="28gioaifgqxio" />} onClick={() => onNavigate('plans')} data-id="element-1174" data-mp="28gsiyjbgdsup">
              See the rules
            </Button>
            <Button icon={<HouseIcon data-id="element-1177" data-mp="1zoqm0v0leup6" />} onClick={() => onNavigate('loan')} data-id="element-1176" data-mp="1zp0c0iln11uv">
              See this on the loan
            </Button>
            <Button icon={<LayoutDashboardIcon data-id="element-1179" data-mp="1kqoi2jpng77e" />} onClick={() => onNavigate('statement')} data-id="element-1178" data-mp="1krga5kk1fmsx">
              See October statement
            </Button>
          </>} toolbar={<div className="space-y-1" data-id="element-1180" data-mp="1brktj07pqzli">
            <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[14px]" data-id="element-1181" data-mp="1bsm8hfruo68i">
              <span data-id="element-1182" data-mp="1gqxgrm2hy8om">
                <strong data-id="element-1183" data-mp="1gqebb4u19crh">Adjusted (pays)</strong> = (<span className="text-sf-purple" data-id="element-1184" data-mp="3652jetvlc8jp">Base BPS</span> + <span className="text-sf-blue-dark" data-id="element-1185" data-mp="365lsrm76dom4">Monthly bonus BPS</span>) ×{' '}
                <span className="text-sf-green-text" data-id="element-1186" data-mp="3b345lnobcnze">Adjusted Loan Amount</span>
              </span>
            </p>
            <p className="text-[13px] tabular-nums" data-id="element-1187" data-mp="3l8lukfz90hia">
              Alex Rivera, LN-2026-004812: 125 bps × $385,692.31 = <strong data-id="element-1188" data-mp="3gak9xqm6ca4p">$4,821.15</strong> adjusted <span className="text-xs text-sf-weak" data-id="element-1189" data-mp="3u0e2aiww1cj6">(bonus 0, main)</span>
            </p>
            <p className="text-[11px] tabular-nums text-sf-weak" data-id="element-1190" data-mp="3u28py4zjniqy">
              Stored (unadjusted) – reference: same BPS × Loan Amount · 125 bps × $412,500.00 = $5,156.25
            </p>
            <div className="pt-1.5" data-id="element-1191" data-mp="7vacwwx55v3s">
              <PaidTotalCallout data-id="element-1192" data-mp="2umevrahwe02" />
            </div>
          </div>} data-id="element-1172" data-mp="4eijmfzvrupx" />

      <FlowRail steps={howSteps} onJump={jumpTo} data-id="element-1193" data-mp="gtvqsero4ly8" />

      <ol className="list-none" data-id="element-1194" data-mp="gtcaf2ho25mb">
        {howSteps.map((step, index) => <StepCard key={step.id} step={step} expanded={expanded.has(step.id)} isLast={index === howSteps.length - 1} onToggle={() => toggle(step.id)} onNavigate={onNavigate} data-id="element-1195" data-mp="cs0qndlh4em1" data-mpi={index}>
            {visuals[step.id]}
          </StepCard>)}
      </ol>

      <CadenceLegend data-id="element-1196" data-mp="hku87pomt3ng" />
    </div>;
}